import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "components/ui/input";
import PhoneNumberInput from "components/ui/phone-input";
import { RootState } from "../../../../redux/store";
import { setNewSubscriptionHostData } from "../../../../redux/slice/subscription/subscriptionSlice";
import { cleanEmail, isEmailAcceptable } from "../../../../util/validation";
import { useEmailFieldFeedback } from "../../common/hooks/useEmailFieldFeedback";

const SubscriptionHostDataStep = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const hostData = useSelector((state: RootState) => state.subscription.newSubscription.hostData);

    // Track touched state to show validation feedback only after user interaction
    const [touched, setTouched] = React.useState({
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
    });

    const markTouched = (field: keyof typeof touched) =>
        setTouched((prev) => ({ ...prev, [field]: true }));

    // Helper to update individual fields
    const updateField = (field: string, value: string) => {
        dispatch(
            setNewSubscriptionHostData({
                firstName: hostData?.firstName || null,
                lastName: hostData?.lastName || null,
                email: hostData?.email || null,
                phone: hostData?.phone || null,
                [field]: field === "email" ? cleanEmail(value) : value,
            })
        );
    };

    const emailFeedback = useEmailFieldFeedback(hostData?.email || "", touched.email);

    // Field validation helpers
    const isFieldValid = (field: keyof typeof touched): boolean => {
        const value = hostData?.[field];
        if (field === "email") return Boolean(value && isEmailAcceptable(value));
        return Boolean(value && value.trim().length > 0);
    };

    return (
        <div className="w-full flex flex-col items-center">
            <div className="w-full max-w-md mx-auto space-y-4 pb-4">
                <div className="space-y-1">
                    <label htmlFor="subscription-first-name" className="text-sm font-medium">
                        {t("book_location.personal_data.first_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="subscription-first-name"
                        value={hostData?.firstName || ""}
                        onChange={(e) => updateField("firstName", e.target.value)}
                        onBlur={() => markTouched("firstName")}
                        autoComplete="given-name"
                        required
                        error={touched.firstName && !isFieldValid("firstName")}
                        aria-invalid={touched.firstName && !isFieldValid("firstName")}
                        aria-describedby="subscription-first-name-error"
                    />
                    {touched.firstName && !isFieldValid("firstName") && (
                        <p id="subscription-first-name-error" className="text-sm text-appRed mt-1">
                            {t("book_location.personal_data.validation.first_name_required", {
                                defaultValue: "Please enter your first name.",
                            })}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="subscription-last-name" className="text-sm font-medium">
                        {t("book_location.personal_data.last_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="subscription-last-name"
                        value={hostData?.lastName || ""}
                        onChange={(e) => updateField("lastName", e.target.value)}
                        onBlur={() => markTouched("lastName")}
                        autoComplete="family-name"
                        required
                        error={touched.lastName && !isFieldValid("lastName")}
                        aria-invalid={touched.lastName && !isFieldValid("lastName")}
                        aria-describedby="subscription-last-name-error"
                    />
                    {touched.lastName && !isFieldValid("lastName") && (
                        <p id="subscription-last-name-error" className="text-sm text-appRed mt-1">
                            {t("book_location.personal_data.validation.last_name_required", {
                                defaultValue: "Please enter your last name.",
                            })}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="subscription-email" className="text-sm font-medium">
                        {t("book_location.personal_data.email")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="subscription-email"
                        type="email"
                        value={hostData?.email || ""}
                        onChange={(e) => updateField("email", e.target.value)}
                        onBlur={() => markTouched("email")}
                        autoComplete="email"
                        required
                        error={!!emailFeedback.error}
                        aria-invalid={!!emailFeedback.error}
                        aria-describedby={
                            emailFeedback.error
                                ? "subscription-email-error"
                                : emailFeedback.warning
                                ? "subscription-email-warning"
                                : undefined
                        }
                    />
                    {emailFeedback.error && (
                        <p id="subscription-email-error" className="text-sm text-appRed mt-1">
                            {emailFeedback.error}
                        </p>
                    )}
                    {emailFeedback.warning && (
                        <p id="subscription-email-warning" className="text-sm text-slate-600 mt-1">
                            {emailFeedback.warning}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="subscription-phone" className="text-sm font-medium">
                        {t("book_location.personal_data.phone_number")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <PhoneNumberInput
                        id="subscription-phone"
                        value={hostData?.phone || ""}
                        onChange={(val) => updateField("phone", val)}
                        onBlur={() => markTouched("phone")}
                        autoComplete="tel"
                        error={touched.phone && !isFieldValid("phone")}
                        aria-invalid={touched.phone && !isFieldValid("phone")}
                        aria-describedby="subscription-phone-error"
                    />
                    {touched.phone && !isFieldValid("phone") && (
                        <p id="subscription-phone-error" className="text-sm text-appRed mt-1">
                            {t("book_location.personal_data.validation.phone_invalid", {
                                defaultValue: "Please enter a valid phone number.",
                            })}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionHostDataStep;
